import { ApolloClient } from '@apollo/client';

import type { InteractiveLessonSectionComponentFragment } from '../lesson-builder.graphql.generated';
import { ComponentColumns } from '../components/component.constants';
import { ColumnData, DroppableComponentData } from './droppable.constants';
import {
  ActivitiesComponentType,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
} from 'global/constants';
import {
  ACTIVITY_BUILDER_CANVAS_ID,
  SectionOrSubSection,
} from '../activity-builder.constants';
import {
  addComponentToColumn,
  addComponentToSection,
  removeComponentFromColumn,
  removeComponentFromSection,
  reorderComponent,
} from '../activity-builder.apollo.utils';

export function canMoveIntoColumn(type: ActivitiesComponentType) {
  return (
    type !== ACTIVITIES_COMPONENT_TYPE_COLUMNS &&
    type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY
  );
}

export function findColumns(
  section: SectionOrSubSection | undefined,
  componentId: string | undefined
) {
  return section?.components.find(
    c => c.id === (componentId || 0)
  ) as ComponentColumns;
}

export function findComponent(
  components: InteractiveLessonSectionComponentFragment[] | undefined,
  id: string
) {
  return components?.find(c => c.id === id);
}

export function moveOutsideColumn(
  client: ApolloClient<any>,
  section: SectionOrSubSection | undefined,
  sort: string,
  item: DroppableComponentData
) {
  const columnComponent = findColumns(section, item.column?.componentId);
  const column = columnComponent.columns?.find(
    col => col.columnId === item.column?.id
  );
  const component = findComponent(column?.components, item.id);
  if (column && component && section) {
    removeComponentFromColumn({ client, componentId: component.id, column });
    addComponentToSection({
      client,
      componentId: component.id,
      componentTypename: component.__typename,
      sectionId: section.id,
    });
    reorderComponent({
      client,
      id: component.id,
      typename: component.__typename,
      sort,
    });
  }
  return {
    newSortValue: sort,
  };
}

export function moveInsideColumn(
  client: ApolloClient<any>,
  section: SectionOrSubSection | undefined,
  sort: string,
  componentData: DroppableComponentData,
  column: ColumnData
) {
  const columnComponent = findColumns(section, column?.componentId);
  const targetColumn = columnComponent.columns?.find(
    col => col.columnId === column.id
  );
  const component = findComponent(section?.components, componentData.id);
  if (targetColumn && component && section) {
    removeComponentFromSection({
      client,
      componentId: componentData.id,
      sectionId: section.id,
    });
    addComponentToColumn({ client, component, column: targetColumn });
    reorderComponent({
      client,
      id: component.id,
      typename: component.__typename,
      sort,
    });
  }
  return {
    columnComponentId: column.componentId,
    columnId: column.id,
    newSortValue: sort,
  };
}

export function moveBetweenColumns(
  client: ApolloClient<any>,
  section: SectionOrSubSection | undefined,
  sort: string,
  sourceColumnItem: {
    id: string;
    column: ColumnData;
  },
  destinationColumn: ColumnData
) {
  const isNewColumnComponent =
    sourceColumnItem.column?.componentId !== destinationColumn.componentId;

  const columnComponent = findColumns(
    section,
    sourceColumnItem.column?.componentId
  );
  const nextColumnComponent =
    isNewColumnComponent && findColumns(section, destinationColumn.componentId);

  const previousColumn = columnComponent.columns?.find(
    col => col.columnId === sourceColumnItem.column?.id
  );

  const nextColumn = nextColumnComponent
    ? nextColumnComponent.columns?.find(
        col => col.columnId === destinationColumn.id
      )
    : columnComponent.columns?.find(
        col => col.columnId === destinationColumn.id
      );

  const component = findComponent(
    previousColumn?.components,
    sourceColumnItem.id
  );
  if (component && previousColumn && nextColumn) {
    reorderComponent({
      client,
      id: component.id,
      typename: component.__typename,
      sort,
    });
    removeComponentFromColumn({
      client,
      componentId: component.id,
      column: previousColumn,
    });
    addComponentToColumn({ client, component, column: nextColumn });
  }
  return {
    columnComponentId: destinationColumn.componentId,
    columnId: destinationColumn.id,
    newSortValue: sort,
  };
}

export function getScrollPosition() {
  const el = document.getElementById(ACTIVITY_BUILDER_CANVAS_ID);
  return el?.scrollTop || 0;
}

export function setScrollPosition(position: number) {
  const el = document.getElementById(ACTIVITY_BUILDER_CANVAS_ID);
  if (el) {
    setTimeout(() => (el.scrollTop = position));
  }
}
